/**
 * Long-lived `pwa.session` root — one traceId per tab session
 * (shape from origin/ts-switch-min `sessionTrace.ts`, Alternative A).
 *
 * Everything useful for support debugging nests here:
 *   pwa.session
 *   ├── request.load / document / send.process (journeys, app.layer=journey)
 *   │     └── app.* diary (process breadcrumbs under ambient journey)
 *   ├── app.* diary (when no journey open)
 *   ├── HTTP (Fetch/XHR)
 *   └── backend spans (when W3C traceparent is accepted)
 *   UI chrome (popups/menus) → root events only (app.layer=ui), not tree rows.
 *
 * See docs/behavior-map/SESSION_TRACE.md for decisions.
 */
import { context, trace } from '@opentelemetry/api';
import { getOtelEnvironment, OTEL_ATTR_DEPLOYMENT_ENVIRONMENT } from './config';
import { getRemoteParentContext, readTraceparentFromHref } from './traceparent';

export const SESSION_SPAN_NAME = 'pwa.session';
export const OTEL_ATTR_PWA_SESSION = 'pwa.session_id';
export const OTEL_ATTR_SESSION_END_REASON = 'session.end_reason';
export const OTEL_ATTR_REQUEST_R = 'request_id';

const TRACER_NAME = 'paperfly-pwa';

/** @type {import('@opentelemetry/api').Span | null} */
let sessionSpan = null;
/** @type {import('@opentelemetry/api').Context | null} */
let sessionContext = null;
let sessionEnded = false;
/** @type {string} */
let sessionRequestId = '';
/** @type {[number, number] | null} hrTime when session root started */
let sessionStartTime = null;
/** @type {Record<string, string | number | boolean> | null} */
let sessionRootAttributes = null;
let sessionRootExportInjected = false;

function hrTimeNow() {
  const now = Date.now();
  return [Math.floor(now / 1000), (now % 1000) * 1e6];
}

export function isSessionTraceActive() {
  return Boolean(sessionSpan && !sessionEnded);
}

export function getSessionContext() {
  return sessionContext || undefined;
}

export function getSessionSpan() {
  if (!sessionSpan || sessionEnded) {
    return null;
  }
  return sessionSpan;
}

export function getSessionTraceId() {
  const span = getSessionSpan();
  return span ? span.spanContext().traceId : null;
}

/**
 * Start a long-lived session span for the current tab.
 * If the URL carries `traceparent` (cross-device / sign-on-mobile QR), the
 * span continues that remote parent (same traceId as the host).
 */
/**
 * Browser facts that explain a stale build: which host served the page, which
 * browser, and whether an (old) service worker controls it.
 */
export function getClientAttributes() {
  if (typeof window === 'undefined') return {};
  const attrs = { 'page.host': window.location.host };
  if (typeof navigator !== 'undefined') {
    if (navigator.userAgent) {
      attrs['user_agent.original'] = navigator.userAgent;
    }
    attrs['app.sw_controlled'] = Boolean(
      'serviceWorker' in navigator && navigator.serviceWorker.controller
    );
  }
  return attrs;
}

export function startSessionTrace(requestId) {
  if (sessionSpan && !sessionEnded) {
    return;
  }

  const tracer = trace.getTracer(TRACER_NAME);
  sessionEnded = false;
  sessionRootExportInjected = false;
  sessionStartTime = hrTimeNow();

  const remoteParent = readTraceparentFromHref();
  const parentCtx = getRemoteParentContext();
  sessionSpan = tracer.startSpan(
    SESSION_SPAN_NAME,
    {
      attributes: remoteParent
        ? {
            'handoff.continued': true,
            'handoff.parent_span_id': remoteParent.spanId
          }
        : undefined
    },
    parentCtx
  );

  const rid = (requestId && String(requestId).trim()) || '';
  if (rid) {
    sessionSpan.setAttribute(OTEL_ATTR_REQUEST_R, rid);
    sessionRequestId = rid;
  }

  const pwaSession =
    typeof window !== 'undefined' ? window.pwaSession : undefined;
  if (pwaSession) {
    sessionSpan.setAttribute(OTEL_ATTR_PWA_SESSION, pwaSession);
  }

  if (typeof window !== 'undefined') {
    sessionSpan.setAttribute('url.path', window.location.pathname);
  }
  const clientAttrs = getClientAttributes();
  Object.keys(clientAttrs).forEach(key =>
    sessionSpan.setAttribute(key, clientAttrs[key])
  );
  sessionSpan.setAttribute(
    OTEL_ATTR_DEPLOYMENT_ENVIRONMENT,
    getOtelEnvironment()
  );
  if (remoteParent) {
    sessionSpan.setAttribute('handoff.continued', true);
    sessionSpan.setAttribute('handoff.parent_span_id', remoteParent.spanId);
  }

  sessionRootAttributes = {
    ...(rid ? { [OTEL_ATTR_REQUEST_R]: rid } : {}),
    ...(pwaSession ? { [OTEL_ATTR_PWA_SESSION]: pwaSession } : {}),
    ...(typeof window !== 'undefined'
      ? { 'url.path': window.location.pathname }
      : {}),
    ...clientAttrs,
    [OTEL_ATTR_DEPLOYMENT_ENVIRONMENT]: getOtelEnvironment(),
    ...(remoteParent
      ? {
          'handoff.continued': true,
          'handoff.parent_span_id': remoteParent.spanId
        }
      : {})
  };

  sessionContext = trace.setSpan(context.active(), sessionSpan);

  context.with(sessionContext, () => {
    if (!sessionSpan) return;
    const startAttrs = {};
    if (rid) startAttrs.request_id = rid;
    if (pwaSession) startAttrs.pwa_session_id = pwaSession;
    if (remoteParent) {
      startAttrs['handoff.continued'] = true;
      startAttrs['handoff.parent_trace_id'] = remoteParent.traceId;
    }
    sessionSpan.addEvent('session.start', startAttrs);
  });

  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(
      '[otel] pwa.session started',
      pwaSession || '(no id)',
      sessionSpan.spanContext().traceId,
      remoteParent ? `(continued from ${remoteParent.spanId})` : '(root)'
    );
  }
}

export function endSessionTrace(reason) {
  const endReason = reason || 'pagehide';
  if (!sessionSpan || sessionEnded) {
    return;
  }
  try {
    sessionSpan.addEvent('session.end', { reason: endReason });
    sessionSpan.setAttribute(OTEL_ATTR_SESSION_END_REASON, endReason);
    sessionSpan.end();
  } catch (e) {
    // ignore
  }
  sessionEnded = true;
  sessionSpan = null;
  sessionContext = null;
  sessionRequestId = '';
  sessionStartTime = null;
  sessionRootAttributes = null;
  sessionRootExportInjected = false;
}

/**
 * Inject open pwa.session into the first OTLP batch so Jaeger has a root
 * (BatchSpanProcessor only exports ended spans; the session root stays open).
 */
export function shouldInjectSessionRoot(spans) {
  if (!isSessionTraceActive() || sessionRootExportInjected || !sessionSpan) {
    return false;
  }
  const sid = sessionSpan.spanContext().spanId;
  return !spans.some(s => s.spanContext().spanId === sid);
}

export function markSessionRootExported() {
  sessionRootExportInjected = true;
}

/** Synthetic ReadableSpan shape for the custom OTLP exporter. */
export function buildSessionRootExportSpan() {
  if (!sessionSpan || !sessionStartTime) {
    return null;
  }
  // Copy events already recorded on the open root (session.start, app.*, …).
  const liveEvents =
    (sessionSpan.events && Array.isArray(sessionSpan.events)
      ? sessionSpan.events
      : []) || [];
  // Preserve remote parent when guest continues host handoff (same traceId).
  const parentSpanContext =
    sessionSpan.parentSpanContext ||
    (sessionSpan.parentSpanId
      ? {
          traceId: sessionSpan.spanContext().traceId,
          spanId: sessionSpan.parentSpanId,
          traceFlags: 1
        }
      : undefined);
  return {
    name: SESSION_SPAN_NAME,
    spanContext: () => sessionSpan.spanContext(),
    parentSpanContext,
    parentSpanId: parentSpanContext && parentSpanContext.spanId,
    startTime: sessionStartTime,
    // Placeholder end — real duration arrives on pagehide when span.end() exports.
    endTime: sessionStartTime,
    attributes: sessionRootAttributes || {},
    events: liveEvents.slice(),
    status: { code: 0 },
    kind: 0
  };
}

/**
 * Update `request_id` on the open pwa.session root (standalone new template, etc.).
 * Emits `request.changed` when the id actually changes.
 */
export function updateSessionRequestId(requestId, detail) {
  const span = getSessionSpan();
  if (!span) return false;

  const rid = (requestId && String(requestId).trim()) || '';
  if (!rid || rid === sessionRequestId) {
    return false;
  }

  const previous = sessionRequestId || '';
  sessionRequestId = rid;
  span.setAttribute(OTEL_ATTR_REQUEST_R, rid);
  if (typeof window !== 'undefined') {
    span.setAttribute('url.path', window.location.pathname);
  }

  const attrs = {
    request_id: rid,
    'request.previous_id': previous,
    ...(detail && typeof detail === 'object' ? detail : {})
  };
  span.addEvent('request.changed', attrs);

  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(
      '[otel] pwa.session request_id',
      previous || '(none)',
      '→',
      rid
    );
  }
  return true;
}

export function getSessionRequestId() {
  return sessionRequestId || null;
}

/** Record analytics as span events on the session root (same trace). */
export function recordSessionEvent(name, attributes) {
  const span = getSessionSpan();
  if (!span) {
    return;
  }
  span.addEvent(name, attributes || {});
}

/** Run fn with session context active (child spans under pwa.session). */
export function runWithSessionContext(fn) {
  const ctx = getSessionContext();
  if (!ctx) {
    return fn();
  }
  return context.with(ctx, fn);
}

/**
 * Short-lived child span under an explicit parent context (journey or session).
 * Used for gae diary so breadcrumbs nest under the open journey in Jaeger.
 */
export function withChildSpan(parentCtx, name, fn) {
  const ctx = parentCtx || sessionContext;
  if (!ctx) {
    return typeof fn === 'function' ? fn(null) : undefined;
  }
  return context.with(ctx, () => {
    const tracer = trace.getTracer(TRACER_NAME);
    if (typeof tracer.startActiveSpan === 'function') {
      return tracer.startActiveSpan(name, span => {
        try {
          return fn(span);
        } finally {
          span.end();
        }
      });
    }
    const span = tracer.startSpan(name, {}, ctx);
    try {
      return fn(span);
    } finally {
      span.end();
    }
  });
}

/**
 * Short-lived child span under the session root (gae diary when no journey).
 */
export function withSessionSpan(name, fn) {
  return withChildSpan(sessionContext, name, fn);
}

/**
 * Force one traceId per tab: orphans and stray roots attach to pwa.session.
 * Journey nesting is kept when parent already shares the session traceId.
 */
export class SessionParentSpanProcessor {
  onStart(span) {
    if (!isSessionTraceActive()) {
      return;
    }

    const root = getSessionSpan();
    if (!root) {
      return;
    }

    const recording = span;
    if (recording.name === SESSION_SPAN_NAME) {
      return;
    }

    const sessionCtx = root.spanContext();
    const currentCtx =
      recording._spanContext ||
      (typeof recording.spanContext === 'function'
        ? recording.spanContext()
        : null);
    if (currentCtx && currentCtx.traceId !== sessionCtx.traceId) {
      recording._spanContext = {
        ...currentCtx,
        traceId: sessionCtx.traceId
      };
    }

    const parent = recording.parentSpanContext;
    // Keep journey (or other in-trace) parents; only reparent true orphans.
    if (!parent || parent.traceId !== sessionCtx.traceId) {
      recording.parentSpanContext = sessionCtx;
    }
  }

  onEnd() {}

  forceFlush() {
    return Promise.resolve();
  }

  shutdown() {
    return Promise.resolve();
  }
}
